import { useMutation, useQuery } from '@apollo/client';
import {
  CheckCircleIcon,
  ClockIcon,
  DocumentMagnifyingGlassIcon,
  PencilSquareIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import {
  UnitTransferSignatureDocumentTypeEnumType,
  UnitTransferSignatureStatusEnumType,
  UnitTransferSignerRoleEnumType,
  UnitTransferSigningModeEnumType,
} from '__generated__/graphql';
import { ElementType, FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';
import SectionCard from 'components/SectionCard';

import Alert from 'primitives/Alert';
import Badge from 'primitives/Badge';
import LoadingIndicator from 'primitives/LoadingIndicator';
import UploadInput from 'primitives/UploadInput';

import { getUser } from 'utils/auth';
import unitTransferSignatureDocumentTypeToReadable from 'utils/enums/unit-transfer-signature-document-type-to-readable';
import unitTransferSignatureStatusToReadable from 'utils/enums/unit-transfer-signature-status-to-readable';

import getUnitTransferRequestTabs from '../tabs';

const UNIT_TRANSFER_REQUEST_SIGNATURES_QUERY = gql(`
  query UnitTransferRequestSignatures($id: ID!) {
    unitTransferRequest(id: $id) {
      id
      transferor {
        id
        name
      }
      transferee {
        id
        name
      }
      signatureRequests {
        id
        documentType
        sequence
        status
        signingMode
        signedFileUrl
        blankTemplateUrl
        sentAt
        completedAt
        signers {
          id
          role
          name
          status
        }
      }
    }
  }
`);

const UPLOAD_MANUALLY_SIGNED_UNIT_TRANSFER_DOCUMENT_MUTATION = gql(`
  mutation UploadManuallySignedUnitTransferDocumentFromInvestorApp($signatureRequestId: ID!, $file: Upload!) {
    uploadManuallySignedUnitTransferDocument(signatureRequestId: $signatureRequestId, file: $file) {
      id
      status
      signedFileUrl
      signers {
        id
        status
        signedAt
      }
    }
  }
`);

type SignatureRequestRow = {
  id: string;
  documentType: UnitTransferSignatureDocumentTypeEnumType;
  sequence: number;
  status: UnitTransferSignatureStatusEnumType;
  signingMode: UnitTransferSigningModeEnumType;
  signedFileUrl?: string | null;
  blankTemplateUrl?: string | null;
  signers: { id: string; role: UnitTransferSignerRoleEnumType }[];
};

// Mirrors unitTransferSignatureStatusToReadable's cases, rendered as a large illustration-style
// badge in the detail panel rather than a small pill.
const getSignatureStatusIcon = (
  status: UnitTransferSignatureStatusEnumType
): { icon: ElementType; badgeBg: string; iconColor: string } => {
  if (status === UnitTransferSignatureStatusEnumType.Signed)
    return { icon: CheckCircleIcon, badgeBg: 'bg-green-50', iconColor: 'text-green-500' };

  if (
    [
      UnitTransferSignatureStatusEnumType.Declined,
      UnitTransferSignatureStatusEnumType.Expired,
      UnitTransferSignatureStatusEnumType.Error,
    ].includes(status)
  )
    return { icon: XCircleIcon, badgeBg: 'bg-red-50', iconColor: 'text-red-500' };

  if (status === UnitTransferSignatureStatusEnumType.PartiallySigned)
    return {
      icon: DocumentMagnifyingGlassIcon,
      badgeBg: 'bg-amber-50',
      iconColor: 'text-amber-500',
    };

  if (status === UnitTransferSignatureStatusEnumType.Sent)
    return { icon: PencilSquareIcon, badgeBg: 'bg-indigo-50', iconColor: 'text-indigo-500' };

  return { icon: ClockIcon, badgeBg: 'bg-gray-100', iconColor: 'text-gray-500' };
};

// One row per signature request in the side list — mirrors the Documents tab's layout. "Not
// yours" is called out here (not just in the detail panel) so the investor can tell at a glance
// which of the six documents are actually theirs to act on.
const SignatureRequestNavItem: FC<{
  signatureRequest: SignatureRequestRow;
  isMine: boolean;
  isSelected: boolean;
  onSelect: () => void;
}> = ({ signatureRequest, isMine, isSelected, onSelect }) => {
  const { label, color } = unitTransferSignatureStatusToReadable(signatureRequest.status);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-3 py-2.5 rounded-md border-l-4 border-t border-r border-b shadow-sm transition-colors ${
        isSelected
          ? 'bg-indigo-50 border-l-indigo-600 border-t-indigo-100 border-r-indigo-100 border-b-indigo-100'
          : 'bg-white border-l-transparent border-t-gray-200 border-r-gray-200 border-b-gray-200 hover:border-l-indigo-200 hover:bg-gray-50'
      }`}
    >
      <p className={`text-sm font-medium ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>
        {signatureRequest.sequence}.{' '}
        {unitTransferSignatureDocumentTypeToReadable(signatureRequest.documentType)}
      </p>
      <div className="mt-1.5 flex items-center gap-1.5">
        <Badge label={label} color={color} showIndicator />
        {!isMine && <span className="text-xs text-gray-400">Not yours</span>}
      </div>
    </button>
  );
};

const SignatureRequestDetailPanel: FC<{
  signatureRequest: SignatureRequestRow;
  isMine: boolean;
  onChanged: () => void;
}> = ({ signatureRequest, isMine, onChanged }) => {
  const { label, color } = unitTransferSignatureStatusToReadable(signatureRequest.status);
  const isManual = signatureRequest.signingMode === UnitTransferSigningModeEnumType.Manual;
  const canUpload =
    isManual && isMine && signatureRequest.status === UnitTransferSignatureStatusEnumType.Sent;
  // NotSent means nothing has actually gone out yet (it's waiting on earlier documents in the
  // sequence, BUS_097) — distinct from Sent/PartiallySigned, where there genuinely is an email or
  // upload step waiting on the signer. Conflating them made NotSent either blank (manual) or show
  // a "check your email" message for an email that was never sent (digital).
  const notYetSent = signatureRequest.status === UnitTransferSignatureStatusEnumType.NotSent;
  const awaitingSignature = [
    UnitTransferSignatureStatusEnumType.Sent,
    UnitTransferSignatureStatusEnumType.PartiallySigned,
  ].includes(signatureRequest.status);
  // "not yet Signed" also matches Declined/Expired/Error — those aren't still awaiting a
  // signature, they've failed, and the request has already reverted to document review.
  const hasFailed = [
    UnitTransferSignatureStatusEnumType.Declined,
    UnitTransferSignatureStatusEnumType.Expired,
    UnitTransferSignatureStatusEnumType.Error,
  ].includes(signatureRequest.status);

  const [uploadSignedDocument, { loading: uploading, error: uploadError }] = useMutation(
    UPLOAD_MANUALLY_SIGNED_UNIT_TRANSFER_DOCUMENT_MUTATION
  );

  const { icon: StatusIcon, badgeBg, iconColor } = getSignatureStatusIcon(signatureRequest.status);

  return (
    <SectionCard
      title={`${signatureRequest.sequence}. ${unitTransferSignatureDocumentTypeToReadable(
        signatureRequest.documentType
      )}`}
      headerContent={<Badge label={label} color={color} showIndicator={true} />}
      bodyClassName="px-4 py-5 sm:p-5 flex items-center gap-4"
    >
      <>
        <div
          className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full ${badgeBg}`}
        >
          <StatusIcon className={`h-6 w-6 ${iconColor}`} />
        </div>

        <div className="min-w-0 flex-1 space-y-3">
          {!isMine && <p className="text-sm text-gray-500">This document is not signed by you.</p>}

          {isMine && notYetSent && (
            <p className="text-sm text-gray-500">
              Not sent yet — it&apos;s waiting on earlier documents in the signing order to be
              signed first.
            </p>
          )}

          {isMine && !isManual && awaitingSignature && (
            <p className="text-sm text-gray-500">
              Awaiting e-signature — check your email for a link from our e-signing partner.
            </p>
          )}

          {isMine && hasFailed && (
            <p className="text-sm text-amber-700">
              This signing request didn&apos;t go through ({label}) — the transfer has moved back to
              document review, and a new request will be sent once that&apos;s resolved.
            </p>
          )}

          {canUpload && (
            <>
              {signatureRequest.blankTemplateUrl && (
                <a
                  className="text-sm text-indigo-700 hover:underline w-fit"
                  href={signatureRequest.blankTemplateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download blank template
                </a>
              )}
              <UploadInput
                label="Upload signed copy"
                value={null}
                maxFileSize={20}
                onChange={([file]) => {
                  if (!file) return;
                  uploadSignedDocument({
                    variables: { signatureRequestId: signatureRequest.id, file },
                  }).then(() => onChanged());
                }}
              />
              {uploading && <p className="text-sm text-gray-500">Uploading…</p>}
            </>
          )}

          {signatureRequest.signedFileUrl && (
            <a
              className="text-sm text-indigo-700 hover:underline w-fit"
              href={signatureRequest.signedFileUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              View signed document
            </a>
          )}

          {uploadError && <ErrorMessage error={uploadError} type="alert" />}
        </div>
      </>
    </SectionCard>
  );
};

const UnitTransferSignatures: FC = () => {
  const { requestId } = useParams<{ requestId: string }>() as { requestId: string };
  const navigate = useNavigate();
  const user = getUser();
  const [selectedSignatureRequestId, setSelectedSignatureRequestId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery(UNIT_TRANSFER_REQUEST_SIGNATURES_QUERY, {
    variables: { id: requestId },
  });

  const renderContent = () => {
    if (loading && !data) return <LoadingIndicator />;
    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const request = data.unitTransferRequest;
    // Same role-gating idiom as the overview tracker (UnitTransferRequest/index.tsx) — mirrors
    // which of the two parties the backend actually lets act on a given document.
    const viewerIsTransferor = request.transferor.id === user.id;
    const viewerRole = viewerIsTransferor
      ? UnitTransferSignerRoleEnumType.Transferor
      : UnitTransferSignerRoleEnumType.Transferee;

    const signatureRequests = [...request.signatureRequests].sort(
      (a, b) => a.sequence - b.sequence
    );

    if (signatureRequests.length === 0)
      return (
        <p className="text-sm text-gray-600">
          Signing has not been initiated for this request yet.
        </p>
      );

    const isMine = (signatureRequest: SignatureRequestRow) =>
      signatureRequest.signers.some(signer => signer.role === viewerRole);

    const allSigned = signatureRequests.every(
      signatureRequest => signatureRequest.status === UnitTransferSignatureStatusEnumType.Signed
    );

    const selectedSignatureRequest =
      signatureRequests.find(sr => sr.id === selectedSignatureRequestId) ?? signatureRequests[0];

    return (
      <div className="space-y-4">
        {allSigned && (
          <Alert
            type="info"
            title="All documents signed"
            message="Every document in this transfer has been signed — there's nothing more to do here."
          />
        )}

        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <div className="flex flex-col gap-2 rounded-lg bg-gray-50 p-3 md:w-72 md:flex-shrink-0">
            {signatureRequests.map(signatureRequest => (
              <SignatureRequestNavItem
                key={signatureRequest.id}
                signatureRequest={signatureRequest}
                isMine={isMine(signatureRequest)}
                isSelected={selectedSignatureRequest.id === signatureRequest.id}
                onSelect={() => setSelectedSignatureRequestId(signatureRequest.id)}
              />
            ))}
          </div>

          <div className="min-w-0 flex-1">
            <SignatureRequestDetailPanel
              signatureRequest={selectedSignatureRequest}
              isMine={isMine(selectedSignatureRequest)}
              onChanged={() => refetch()}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Signature Documents"
        subTitle="Track and complete the documents that require your signature"
        secondaryTabs={getUnitTransferRequestTabs(requestId)}
        onBackButtonClick={() => navigate(`/invest/unit-transfers/${requestId}`)}
      />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default UnitTransferSignatures;
